import { isEqual } from 'lodash';
import type { Map } from 'mapbox-gl';

/** Mapbox APIs used to inspect and reorder style layers. */
export type MapLayerStack = {
  getLayer: (id: string) => unknown;
  getStyle: () => { layers?: ReadonlyArray<{ id: string }> };
  moveLayer: (id: string, beforeId?: string) => unknown;
};

type LayoutPropertyName = Parameters<Map['setLayoutProperty']>[1];
type LayoutPropertyValue = Parameters<Map['setLayoutProperty']>[2];

/**
 * Layer ids in current style order (bottom → top).
 */
export const getMapLayerIds = (map: MapLayerStack): string[] =>
  (map.getStyle().layers ?? []).map((layer) => layer.id);

/**
 * True when every id in {@link layerIds} currently exists on the map.
 * Uses {@link MapLayerStack.getLayer} so callers can skip {@link getMapLayerIds} / {@link Map.moveLayer} on style events.
 */
export const areMapLayersPresent = (
  map: Pick<MapLayerStack, 'getLayer'>,
  layerIds: readonly string[],
): boolean => layerIds.every((id) => Boolean(map.getLayer(id)));

/**
 * True when {@link layerId} already sits immediately below {@link beforeId}.
 */
export const isLayerImmediatelyBefore = (
  layerIds: readonly string[],
  layerId: string,
  beforeId: string,
): boolean => {
  const layerIndex = layerIds.indexOf(layerId);
  const beforeIndex = layerIds.indexOf(beforeId);
  if (layerIndex < 0 || beforeIndex < 0) return false;
  return layerIndex === beforeIndex - 1;
};

/**
 * True when {@link layerId} is already the topmost style layer.
 */
export const isTopLayer = (
  layerIds: readonly string[],
  layerId: string,
): boolean => {
  if (layerIds.length === 0) return false;
  return layerIds[layerIds.length - 1] === layerId;
};

/**
 * True when two Mapbox paint/layout values should be treated as already applied.
 */
export const areStylePropertyValuesEqual = (
  left: unknown,
  right: unknown,
): boolean => isEqual(left, right);

/**
 * {@link Map.moveLayer} only when the layer is not already in that slot.
 * `beforeId` omitted moves the layer to the top.
 */
export const moveLayerBeforeIfNeeded = (
  map: MapLayerStack,
  layerId: string,
  beforeId: string | undefined,
): boolean => {
  if (!map.getLayer(layerId)) return false;
  const layerIds = getMapLayerIds(map);
  if (!beforeId) {
    if (isTopLayer(layerIds, layerId)) return false;
    map.moveLayer(layerId);
    return true;
  }
  if (!map.getLayer(beforeId)) return false;
  if (isLayerImmediatelyBefore(layerIds, layerId, beforeId)) return false;
  map.moveLayer(layerId, beforeId);
  return true;
};

/**
 * Place {@link layerIds} bottom → top, with the last present id on top of the map.
 */
export const stackLayersOnTop = (
  map: MapLayerStack,
  layerIds: readonly string[],
): void => {
  const present = layerIds.filter((id) => map.getLayer(id));
  const topId = present[present.length - 1];
  if (!topId) return;
  moveLayerBeforeIfNeeded(map, topId, undefined);
  stackAdjacentLayers(map, present);
};

/**
 * Place {@link layerIds} bottom → top immediately under {@link beforeId} when it exists.
 */
export const stackLayersBefore = (
  map: MapLayerStack,
  layerIds: readonly string[],
  beforeId: string | undefined,
): void => {
  const present = layerIds.filter((id) => map.getLayer(id));
  const topId = present[present.length - 1];
  if (!topId) return;
  if (beforeId && map.getLayer(beforeId)) {
    moveLayerBeforeIfNeeded(map, topId, beforeId);
  }
  stackAdjacentLayers(map, present);
};

/**
 * Set a layout property only when Mapbox does not already hold that value.
 */
export const setLayoutPropertyIfChanged = (
  map: Map,
  layerId: string,
  property: LayoutPropertyName,
  value: LayoutPropertyValue,
): boolean => {
  if (!map.getLayer(layerId)) return false;
  const current = map.getLayoutProperty(layerId, property);
  if (areStylePropertyValuesEqual(current, value)) return false;
  map.setLayoutProperty(layerId, property, value);
  return true;
};

/**
 * Toggle `visibility` only when it would change. Unset is treated as visible.
 */
export const setLayerVisibilityIfChanged = (
  map: Map,
  layerId: string,
  visible: boolean,
): boolean => {
  if (!map.getLayer(layerId)) return false;
  const desired = visible ? 'visible' : 'none';
  const current = map.getLayoutProperty(layerId, 'visibility');
  const normalized = current === 'none' ? 'none' : 'visible';
  if (normalized === desired) return false;
  map.setLayoutProperty(layerId, 'visibility', desired);
  return true;
};

/**
 * Make each id sit immediately below the next id in {@link present}.
 */
const stackAdjacentLayers = (
  map: MapLayerStack,
  present: readonly string[],
): void => {
  for (let index = present.length - 2; index >= 0; index -= 1) {
    const layerId = present[index];
    const aboveId = present[index + 1];
    if (!layerId || !aboveId) continue;
    moveLayerBeforeIfNeeded(map, layerId, aboveId);
  }
};
